import React, { useEffect, useState, useRef, useCallback } from "react";
import { API } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

function SlackIdCell({ user, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(user.slack_user_id || "");
  const inputRef = useRef(null);

  const save = () => {
    onSave(user.id, val.trim());
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <input
          ref={inputRef}
          className="input"
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          placeholder="U0123456789"
          style={{ fontSize: 12, padding: "4px 8px", width: 130 }}
          autoFocus
        />
        <button className="btn btn-sm btn-primary" onClick={save} style={{ fontSize: 11, padding: "4px 8px" }}>✓</button>
        <button className="btn btn-sm btn-secondary" onClick={() => setEditing(false)} style={{ fontSize: 11, padding: "4px 8px" }}>✕</button>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 12, color: user.slack_user_id ? "var(--text-secondary)" : "var(--text-muted)", fontFamily: "monospace" }}>
        {user.slack_user_id || "—"}
      </span>
      <button
        className="btn btn-sm btn-ghost"
        onClick={() => { setVal(user.slack_user_id || ""); setEditing(true); }}
        style={{ fontSize: 11, padding: "2px 6px" }}
      >
        {user.slack_user_id ? "✏️" : "+ Agregar"}
      </button>
    </div>
  );
}

const ROLES = [
  { value: "super_admin",  label: "Super Admin" },
  { value: "team_leader",  label: "Team Leader" },
  { value: "member",       label: "Member" },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function fmtMinutes(mins) {
  if (!mins || mins === 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

function roleLabel(role) {
  if (role === "super_admin") return "Super Admin";
  if (role === "team_leader") return "Team Leader";
  return "Member";
}

// ── Modal: desglose por agente ────────────────────────────────────────────────
function AgentDetailModal({ user, onClose }) {
  if (!user) return null;

  const maxMsgs = Math.max(...(user.agents_detail || []).map(a => a.total_messages), 1);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          width: "100%", maxWidth: 520,
          maxHeight: "80vh",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          {user.avatar
            ? <img src={user.avatar} alt={user.name} style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0 }} />
            : <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--accent-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "var(--accent)", flexShrink: 0 }}>{user.name?.[0]}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{user.name}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {user.total_messages} mensajes · {fmtMinutes(user.estimated_minutes)} estimado
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 20, lineHeight: 1, padding: "2px 4px" }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "12px 0" }}>
          {!user.agents_detail || user.agents_detail.length === 0 ? (
            <div style={{ padding: "32px 20px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
              Sin datos de agentes en este período
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Agente", "Mensajes", "Tiempo est."].map(h => (
                    <th key={h} style={{
                      padding: "6px 20px 10px",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {user.agents_detail.map((agent, i) => {
                  const pct = Math.round((agent.total_messages / maxMsgs) * 100);
                  return (
                    <tr key={agent.agent_id} style={{ borderTop: i === 0 ? "1px solid var(--border)" : "1px solid var(--border)" }}>
                      {/* Agent name + bar */}
                      <td style={{ padding: "12px 20px", minWidth: 160 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", marginBottom: 5 }}>
                          {agent.agent_name}
                        </div>
                        <div style={{ height: 4, borderRadius: 2, background: "var(--bg-hover)", overflow: "hidden" }}>
                          <div style={{
                            height: "100%",
                            width: `${pct}%`,
                            background: "var(--accent)",
                            borderRadius: 2,
                            opacity: 0.7,
                          }} />
                        </div>
                      </td>
                      {/* Messages */}
                      <td style={{ padding: "12px 20px", verticalAlign: "top" }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
                          {agent.total_messages.toLocaleString("es-AR")}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {agent.total_sessions} {agent.total_sessions === 1 ? "sesión" : "sesiones"}
                        </div>
                      </td>
                      {/* Time */}
                      <td style={{ padding: "12px 20px", verticalAlign: "top" }}>
                        <span style={{
                          fontSize: 13, fontWeight: 500,
                          color: agent.estimated_minutes > 0 ? "var(--accent)" : "var(--text-muted)",
                        }}>
                          {fmtMinutes(agent.estimated_minutes)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer note */}
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--border)" }}>
          <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)" }}>
            * Tiempo estimado por agente calculado independientemente (ventana 30 min + 5 min buffer/sesión).
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Activity tab ───────────────────────────────────────────────────────────────
function ActivityTab() {
  const [from, setFrom]           = useState(daysAgo(29));
  const [to, setTo]               = useState(today());
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [sortKey, setSortKey]     = useState("total_messages");
  const [sortDir, setSortDir]     = useState("desc");
  const [detailUser, setDetailUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get(`/api/users/activity?from=${from}&to=${to}`);
      setData(r.data);
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Close modal on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setDetailUser(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sorted = data ? [...data].sort((a, b) => {
    const va = a[sortKey] ?? 0;
    const vb = b[sortKey] ?? 0;
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb, "es") : vb.localeCompare(va, "es");
    return sortDir === "asc" ? va - vb : vb - va;
  }) : [];

  const cols = [
    { key: "name",               label: "Usuario",      sortable: true  },
    { key: "total_messages",     label: "Mensajes",     sortable: true  },
    { key: "total_sessions",     label: "Sesiones",     sortable: true  },
    { key: "estimated_minutes",  label: "Tiempo est.",  sortable: true  },
    { key: "active_days",        label: "Días activos", sortable: true  },
    { key: "top_agent",          label: "Agente top",   sortable: false },
    { key: "_detail",            label: "",             sortable: false },
  ];

  const SortIcon = ({ col }) => {
    if (!col.sortable) return null;
    if (sortKey !== col.key) return <span style={{ color: "var(--text-muted)", marginLeft: 4, opacity: 0.4 }}>⇅</span>;
    return <span style={{ marginLeft: 4, color: "var(--accent)" }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <div>
      {/* Agent detail modal */}
      <AgentDetailModal user={detailUser} onClose={() => setDetailUser(null)} />

      {/* Date range filter */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label className="label">Desde</label>
          <input type="date" className="input" value={from}
            onChange={e => setFrom(e.target.value)}
            style={{ width: 160 }} />
        </div>
        <div>
          <label className="label">Hasta</label>
          <input type="date" className="input" value={to}
            onChange={e => setTo(e.target.value)}
            style={{ width: 160 }} />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(daysAgo(6));  setTo(today()); }}>7 días</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(daysAgo(29)); setTo(today()); }}>30 días</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(daysAgo(89)); setTo(today()); }}>90 días</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 64 }}><div className="spinner" /></div>
      ) : !data ? (
        <div className="empty-state"><h3>Error al cargar datos</h3></div>
      ) : sorted.length === 0 ? (
        <div className="empty-state"><h3>Sin actividad en este período</h3></div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="card activity-table-wrapper" style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {cols.map(col => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && handleSort(col.key)}
                      style={{
                        padding: "12px 20px",
                        textAlign: "left",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--text-muted)",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        cursor: col.sortable ? "pointer" : "default",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}<SortIcon col={col} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(user => (
                  <tr key={user.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    {/* Usuario */}
                    <td style={{ padding: "14px 20px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {user.avatar
                          ? <img src={user.avatar} alt={user.name} style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
                          : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--accent-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: "var(--accent)", flexShrink: 0 }}>{user.name?.[0]}</div>
                        }
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{user.name}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{roleLabel(user.role)}</div>
                        </div>
                      </div>
                    </td>
                    {/* Mensajes */}
                    <td style={{ padding: "14px 20px" }}>
                      {user.total_messages > 0 ? (
                        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
                          {user.total_messages.toLocaleString("es-AR")}
                        </span>
                      ) : (
                        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    {/* Sesiones */}
                    <td style={{ padding: "14px 20px", fontSize: 13, color: "var(--text-secondary)" }}>
                      {user.total_sessions > 0 ? user.total_sessions : "—"}
                    </td>
                    {/* Tiempo estimado */}
                    <td style={{ padding: "14px 20px" }}>
                      <span style={{ fontSize: 13, fontWeight: user.estimated_minutes > 0 ? 500 : 400, color: user.estimated_minutes > 0 ? "var(--accent)" : "var(--text-muted)" }}>
                        {fmtMinutes(user.estimated_minutes)}
                      </span>
                    </td>
                    {/* Días activos */}
                    <td style={{ padding: "14px 20px", fontSize: 13, color: "var(--text-secondary)" }}>
                      {user.active_days > 0 ? (
                        <span>{user.active_days} {user.active_days === 1 ? "día" : "días"}</span>
                      ) : "—"}
                    </td>
                    {/* Agente top */}
                    <td style={{ padding: "14px 20px" }}>
                      {user.top_agent ? (
                        <span style={{ fontSize: 12, background: "var(--bg-hover)", color: "var(--text-secondary)", padding: "3px 8px", borderRadius: "var(--radius-sm)", whiteSpace: "nowrap" }}>
                          {user.top_agent}
                        </span>
                      ) : (
                        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    {/* Detalle */}
                    <td style={{ padding: "14px 16px", textAlign: "right" }}>
                      {user.total_messages > 0 && (
                        <button
                          onClick={() => setDetailUser(user)}
                          title="Ver desglose por agente"
                          style={{
                            background: "none",
                            border: "1px solid var(--border)",
                            borderRadius: "var(--radius-sm)",
                            cursor: "pointer",
                            padding: "4px 8px",
                            fontSize: 14,
                            color: "var(--text-muted)",
                            lineHeight: 1,
                            transition: "border-color 0.15s, color 0.15s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-muted)"; }}
                        >
                          📊
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="activity-mobile-cards">
            {sorted.map(user => (
              <div key={user.id} className="card" style={{ padding: 16, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  {user.avatar
                    ? <img src={user.avatar} alt={user.name} style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0 }} />
                    : <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--accent-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 600, color: "var(--accent)", flexShrink: 0 }}>{user.name?.[0]}</div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{user.name}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{roleLabel(user.role)}</div>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: "var(--accent)" }}>{fmtMinutes(user.estimated_minutes)}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div style={{ textAlign: "center", background: "var(--bg-hover)", borderRadius: "var(--radius-sm)", padding: "8px 4px" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>{user.total_messages || "—"}</div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.3px" }}>Mensajes</div>
                  </div>
                  <div style={{ textAlign: "center", background: "var(--bg-hover)", borderRadius: "var(--radius-sm)", padding: "8px 4px" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>{user.total_sessions || "—"}</div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.3px" }}>Sesiones</div>
                  </div>
                  <div style={{ textAlign: "center", background: "var(--bg-hover)", borderRadius: "var(--radius-sm)", padding: "8px 4px" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>{user.active_days || "—"}</div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.3px" }}>Días</div>
                  </div>
                </div>
                {/* Footer: agente top + botón detalle */}
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  {user.top_agent ? (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      Agente top: <span style={{ color: "var(--text-secondary)", fontWeight: 500 }}>{user.top_agent}</span>
                    </div>
                  ) : <div />}
                  {user.total_messages > 0 && (
                    <button
                      onClick={() => setDetailUser(user)}
                      className="btn btn-sm btn-secondary"
                      style={{ fontSize: 12, padding: "4px 10px" }}
                    >
                      📊 Ver detalle
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p style={{ marginTop: 12, fontSize: 11, color: "var(--text-muted)" }}>
            * Tiempo estimado: suma de sesiones (ventana de 30 min entre mensajes) + 5 min de buffer por sesión.
          </p>
        </>
      )}
    </div>
  );
}

export default function Users() {
  const toast = useToast();
  const [tab, setTab]   = useState("usuarios");
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/api/users")
      .then(r => setUsers(r.data))
      .finally(() => setLoading(false));
  }, []);

  const handleRoleChange = async (id, role) => {
    try {
      const r = await API.patch(`/api/users/${id}/role`, { role });
      setUsers(prev => prev.map(u => u.id === id ? r.data : u));
      toast("Rol actualizado", "success");
    } catch {
      toast("Error al actualizar rol", "error");
    }
  };

  const handleToggleActive = async (id, active) => {
    try {
      const r = await API.patch(`/api/users/${id}/active`, { active: !active });
      setUsers(prev => prev.map(u => u.id === id ? r.data : u));
      toast(active ? "Usuario desactivado" : "Usuario activado", "success");
    } catch {
      toast("Error", "error");
    }
  };

  const handleSlackId = async (id, slack_user_id) => {
    try {
      const r = await API.patch(`/api/users/${id}/slack`, { slack_user_id });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, slack_user_id: r.data.slack_user_id } : u));
      toast("Slack ID guardado", "success");
    } catch {
      toast("Error al guardar Slack ID", "error");
    }
  };

  const tabStyle = (t) => ({
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 500,
    border: "none",
    background: "none",
    cursor: "pointer",
    borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
    color: tab === t ? "var(--accent)" : "var(--text-muted)",
    transition: "color 0.15s, border-color 0.15s",
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Usuarios</h1>
        <p className="page-subtitle">{users.length} usuarios registrados</p>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid var(--border)", marginBottom: 24, display: "flex", gap: 0, paddingLeft: 24 }}>
        <button style={tabStyle("usuarios")} onClick={() => setTab("usuarios")}>Usuarios</button>
        <button style={tabStyle("actividad")} onClick={() => setTab("actividad")}>Actividad</button>
      </div>

      <div className="page-body" style={{ paddingTop: 0 }}>
        {tab === "usuarios" && (
          <>
            {loading ? (
              <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" /></div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="card users-table-wrapper" style={{ padding: 0, overflow: "hidden" }}>
                  <table className="users-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid var(--border)" }}>
                        {["Usuario", "Email", "Slack ID", "Rol", "Estado", "Acciones"].map(h => (
                          <th key={h} style={{ padding: "12px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td data-label="Usuario" style={{ padding: "14px 20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              {user.avatar
                                ? <img src={user.avatar} alt={user.name} style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
                                : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--accent-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: "var(--accent)", flexShrink: 0 }}>{user.name?.[0]}</div>
                              }
                              <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)" }}>{user.name}</span>
                            </div>
                          </td>
                          <td data-label="Email" style={{ padding: "14px 20px", fontSize: 13, color: "var(--text-secondary)" }}>{user.email}</td>
                          <td data-label="Slack ID" style={{ padding: "14px 20px" }}>
                            <SlackIdCell user={user} onSave={handleSlackId} />
                          </td>
                          <td data-label="Rol" style={{ padding: "14px 20px" }}>
                            <select
                              className="select"
                              value={user.role}
                              onChange={e => handleRoleChange(user.id, e.target.value)}
                              style={{ fontSize: 13, padding: "6px 10px" }}
                            >
                              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                          </td>
                          <td data-label="Estado" style={{ padding: "14px 20px" }}>
                            <span className={`badge ${user.active ? "badge-green" : "badge-gray"}`}>
                              {user.active ? "Activo" : "Inactivo"}
                            </span>
                          </td>
                          <td data-label="Acciones" style={{ padding: "14px 20px" }}>
                            <button
                              className={`btn btn-sm ${user.active ? "btn-danger" : "btn-secondary"}`}
                              onClick={() => handleToggleActive(user.id, user.active)}
                            >
                              {user.active ? "Desactivar" : "Activar"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="users-mobile-cards">
                  {users.map(user => (
                    <div key={user.id} className="card" style={{ padding: "16px", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                        {user.avatar
                          ? <img src={user.avatar} alt={user.name} style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0 }} />
                          : <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--accent-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 600, color: "var(--accent)", flexShrink: 0 }}>{user.name?.[0]}</div>
                        }
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{user.name}</div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
                        </div>
                        <span className={`badge ${user.active ? "badge-green" : "badge-gray"}`}>
                          {user.active ? "Activo" : "Inactivo"}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <select
                          className="select"
                          value={user.role}
                          onChange={e => handleRoleChange(user.id, e.target.value)}
                          style={{ fontSize: 13, flex: 1 }}
                        >
                          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        <button
                          className={`btn btn-sm ${user.active ? "btn-danger" : "btn-secondary"}`}
                          onClick={() => handleToggleActive(user.id, user.active)}
                          style={{ flexShrink: 0 }}
                        >
                          {user.active ? "Desactivar" : "Activar"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {tab === "actividad" && <ActivityTab />}
      </div>
    </div>
  );
}
